// AdminBillingPage.jsx
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { MainContainer } from '../MainContainer'
import {
    getAllTransactionsAdmin,
    searchTransactionsAdmin,
    savePromoCodeAdmin,
    getAllPromosAdmin,
    searchPromosAdmin,
    togglePromoCodeAdmin,
    archivePromoCodeAdmin
} from '../../actions/adminBilling.action'
import { searchUser } from '../../actions/user.actions'
import Loader from '../../components/Loader'
import Modal from '../../components/UI/Modal'
import ModalConfirm from '../../components/BurgerMenu/ModalConfirm'
import AdminFinanceNav from '../../components/AdminFinanceNav'
import { fetchBillingSettings, saveBillingSettings } from '../../api/adminPaymentsApi'

const PROMO_FILTERS = [
    { value: 'all', label: 'Все кроме архива' },
    { value: 'active', label: 'Активные' },
    { value: 'inactive', label: 'Отключённые' },
    { value: 'archived', label: 'Архив' },
]

const PROMO_SCOPE_LABELS = {
    balance: 'Баланс',
    topup: 'Пополнение',
    subscription: 'Подписка',
    scene: '3D-сцена',
    any: 'Любая область',
}

const TRANSACTION_TYPE_LABELS = {
    TINKOFF_IN: 'Пополнение картой',
    QR_IN: 'Пополнение QR',
    ADMIN_IN: 'Начисление администратором',
    ADMIN_OUT: 'Списание администратором',
    TRANSFER_TO_OFFICE: 'Перевод в офис',
    PAYMENT_FOR_SCENE: 'Legacy-разовая 3D-сцена',
    SCENE_CREATE: 'Создание 3D-сцены',
    SCENE_PACKAGE: 'Пакет 3D-сцен',
    STORAGE_PACKAGE: 'Дополнительное хранилище',
    SUBSCRIPTION_FEE: 'Подписка офиса',
    DIGITAL_PURCHASE: 'Цифровая покупка',
}

const TRANSACTION_STATUS_LABELS = {
    PENDING: 'Ожидает',
    SUCCESS: 'Успешно',
    FAILED: 'Ошибка',
}

const formatMoney = (value) => {
    const number = Number(value)

    if (!Number.isFinite(number)) return '—'

    return `${number.toLocaleString('ru-RU', {
        minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
        maximumFractionDigits: 2,
    })} ₽`
}

const AdminBillingPage = () => {
    const dispatch = useDispatch()
    const {
        transactions = [],
        promos = [],
        analytics = {},
        loading = false,
        totalAll = 0,
        totalPages = 0,
        error = null,
    } = useSelector(state => state.adminBilling || {})

    const usersList = useSelector(state => state.user?.users || [])

    const [activeTab, setActiveTab] = useState('promos')
    const [statusFilter, setStatusFilter] = useState('all')
    const [pageNumber, setPageNumber] = useState(0)
    const [pageSize, setPageSize] = useState(20)
    const [selectedPromoDetails, setSelectedPromoDetails] = useState(null)
    const [showPromoModal, setShowPromoModal] = useState(false)
    const [archivePromo, setArchivePromo] = useState(null)
    const [billingSettings, setBillingSettings] = useState(null)
    const [billingSettingsLoading, setBillingSettingsLoading] = useState(false)
    const [billingSettingsError, setBillingSettingsError] = useState('')
    const [billingSettingsMessage, setBillingSettingsMessage] = useState('')

    const [promoForm, setPromoForm] = useState({
        _id: null,
        code: '',
        type: 'FIXED',
        amount: '',
        subscriptionDays: '',
        maxUses: 1,
        expireValue: '',
        expireUnit: 'days',
        targetUserId: '',
        source: '',
        minTopUpAmount: '',
        scope: 'balance'
    })

    const [userSearchText, setUserSearchText] = useState('')
    const [searchMainName, setSearchMainName] = useState('')
    const [selectedUser, setSelectedUser] = useState(null)

    useEffect(() => {
        setPageNumber(0)
        setSelectedPromoDetails(null)
    }, [activeTab, statusFilter])

    useEffect(() => {
        let cancelled = false

        setBillingSettingsLoading(true)
        setBillingSettingsError('')

        fetchBillingSettings()
            .then((settings) => {
                if (cancelled) return

                if (!settings) {
                    setBillingSettingsError('Backend не вернул настройки биллинга')
                    return
                }

                setBillingSettings(settings)
            })
            .catch((error) => {
                if (cancelled) return

                setBillingSettingsError(
                    error?.response?.data?.error
                    || error?.response?.data?.message
                    || error?.message
                    || 'Не удалось загрузить настройки биллинга'
                )
            })
            .finally(() => {
                if (!cancelled) setBillingSettingsLoading(false)
            })

        return () => { cancelled = true }
    }, [])

    const additionalData = useMemo(() => ({
        activeTab,
        statusFilter,
        pageSize,
        searchMainName
    }), [activeTab, statusFilter, pageSize, searchMainName])

    const getAction = activeTab === 'transactions'
        ? getAllTransactionsAdmin
        : activeTab === 'promos'
            ? getAllPromosAdmin
            : null

    const searchAction = activeTab === 'transactions'
        ? searchTransactionsAdmin
        : activeTab === 'promos'
            ? searchPromosAdmin
            : null

    const typeSort = activeTab === 'transactions'
        ? 'transaction'
        : activeTab === 'promos'
            ? 'promo'
            : 'settings'

    const promoValueIsValid = promoForm.type !== 'SUBSCRIPTION' && Number(promoForm.amount) > 0

    const promoFormIsValid = Boolean(
        String(promoForm.code || '').trim()
        && promoValueIsValid
        && Number(promoForm.maxUses) >= 0
        && (
            promoForm.expireValue === ''
            || (
                Number.isFinite(Number(promoForm.expireValue))
                && Number(promoForm.expireValue) >= 0
            )
        )
    )

    const resetPromoForm = () => {
        setPromoForm({
            _id: null,
            code: '',
            type: 'FIXED',
            amount: '',
            subscriptionDays: '',
            maxUses: 1,
            expireValue: '',
            expireUnit: 'days',
            targetUserId: '',
            source: '',
            minTopUpAmount: '',
            scope: 'balance'
        })
        setSelectedUser(null)
        setUserSearchText('')
    }

    const handleOpenCreate = () => {
        resetPromoForm()
        setShowPromoModal(true)
    }

    const handleOpenEdit = (promo) => {
        setPromoForm({
            _id: promo._id,
            code: promo.code,
            type: promo.type || 'FIXED',
            amount: promo.amount || '',
            subscriptionDays: promo.subscriptionDays || '',
            maxUses: promo.maxUses,
            expireValue: '',
            expireUnit: 'days',
            targetUserId: promo.targetUser?._id || '',
            source: promo.source || '',
            minTopUpAmount: promo.minTopUpAmount || '',
            scope: promo.scope || 'balance'
        })
        setSelectedUser(promo.targetUser || null)
        setUserSearchText('')
        setShowPromoModal(true)
    }

    const handleClosePromo = () => {
        if (loading) return
        setShowPromoModal(false)
        setUserSearchText('')
    }

    const handlePromoTypeChange = (type) => {
        let scope = 'balance'

        if (type === 'PERCENTAGE') scope = 'topup'
        if (type === 'SUBSCRIPTION') scope = 'subscription'
        if (type === 'DISCOUNT') scope = 'scene'

        setPromoForm({
            ...promoForm,
            type,
            scope
        })
    }

    const handleSavePromo = async () => {
        if (!promoFormIsValid || loading) return

        const payload = {
            ...promoForm,
            code: String(promoForm.code || '').trim().toUpperCase(),
            source: String(promoForm.source || '').trim(),
            targetUserId: selectedUser ? selectedUser._id : null
        }

        const success = await dispatch(savePromoCodeAdmin(payload, additionalData))

        if (success) {
            setShowPromoModal(false)
            setUserSearchText('')
        }
    }

    const handleTogglePromo = (id) => {
        dispatch(togglePromoCodeAdmin(id, additionalData))
    }

    const handleArchivePromo = (promo) => {
        setArchivePromo(promo)
    }

    const handleCloseArchiveConfirm = () => {
        if (loading) return
        setArchivePromo(null)
    }

    const handleConfirmArchive = async () => {
        if (!archivePromo?._id || loading) return

        const success = await dispatch(
            archivePromoCodeAdmin(archivePromo._id, additionalData)
        )

        if (success) {
            setArchivePromo(null)
        }
    }

    const handleUserSearch = (e) => {
        const text = e.target.value
        setUserSearchText(text)

        if (text.length > 2) {
            dispatch(searchUser(text))
        }
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '—'

        const d = new Date(dateStr)

        if (Number.isNaN(d.getTime())) return '—'

        return d.toLocaleDateString('ru-RU') + ' ' + d.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const getPromoPresentation = (promo) => {
        const usedBy = Array.isArray(promo?.usedBy) ? promo.usedBy : []
        const isExpired = Boolean(promo?.expiresAt && new Date(promo.expiresAt) < new Date())
        const isExhausted = Number(promo?.maxUses) > 0 && usedBy.length >= Number(promo.maxUses)

        const statusText = promo?.isArchived
            ? 'Архив'
            : !promo?.isActive
                ? 'Отключён'
                : isExpired
                    ? 'Истёк'
                    : isExhausted
                        ? 'Исчерпан'
                        : 'Активен'

        const statusTone = promo?.isArchived
            ? 'neutral'
            : promo?.isActive && !isExpired && !isExhausted
                ? 'success'
                : 'danger'

        const typeLabel = promo?.type === 'PERCENTAGE'
            ? `${promo.amount}% к пополнению`
            : promo?.type === 'SUBSCRIPTION'
                ? `${promo.subscriptionDays} дней`
                : promo?.type === 'DISCOUNT'
                    ? `${formatMoney(promo.amount)} скидка`
                    : formatMoney(promo?.amount)

        return { usedBy, statusText, statusTone, typeLabel }
    }

    const renderTransactionStatus = (status) => {
        const normalized = String(status || '').toUpperCase()
        const label = TRANSACTION_STATUS_LABELS[normalized] || normalized || '—'

        return (
            <span className={`admin-billing__status-chip admin-billing__status-chip--${normalized.toLowerCase() || 'neutral'}`}>
                {label}
            </span>
        )
    }

    const clearBillingSettingsFeedback = () => {
        setBillingSettingsError('')
        setBillingSettingsMessage('')
    }

    const patchBillingSettings = (key, value) => {
        clearBillingSettingsFeedback()
        setBillingSettings((prev) => ({
            ...(prev || {}),
            [key]: value,
        }))
    }

    const patchScenePackage = (index, key, value) => {
        clearBillingSettingsFeedback()
        setBillingSettings((prev) => ({
            ...(prev || {}),
            scenePackages: (prev?.scenePackages || []).map((item, itemIndex) => (
                itemIndex === index ? { ...item, [key]: value } : item
            )),
        }))
    }

    const billingSettingsValidationError = useMemo(() => {
        if (!billingSettings) return ''

        const checks = [
            ['Подписка офиса', billingSettings.monthlyPrice, 0],
            ['Длительность периода', billingSettings.periodDays, 1],
            ['Сцен включено', billingSettings.includedScenes, 0],
            ['Цена дополнительной сцены', billingSettings.extraScenePrice, 0],
            ['Кредитный лимит', billingSettings.defaultCreditLimit, 0],
            ['Максимум сотрудников', billingSettings.maxWorkers, 1],
            ['Базовое хранилище', billingSettings.includedStorageGb, 1],
            ['Цена дополнительного места', billingSettings.extraStoragePricePerGb, 0],
        ]

        for (const [label, value, min] of checks) {
            const number = Number(value)

            if (!Number.isFinite(number) || number < min) {
                return `${label}: значение должно быть не меньше ${min}`
            }
        }

        const packages = Array.isArray(billingSettings.scenePackages)
            ? billingSettings.scenePackages
            : []

        if (!packages.length) {
            return 'Нужно оставить хотя бы один пакет сцен'
        }

        for (const item of packages) {
            if (!String(item?.key || '').trim()) {
                return 'У каждого пакета должен быть системный ключ'
            }

            if (!String(item?.title || '').trim()) {
                return 'У каждого пакета должно быть название'
            }

            if (!Number.isFinite(Number(item?.scenes)) || Number(item.scenes) < 1) {
                return `Пакет «${item?.title || item?.key}»: количество сцен должно быть не меньше 1`
            }

            if (!Number.isFinite(Number(item?.price)) || Number(item.price) < 0) {
                return `Пакет «${item?.title || item?.key}»: цена не может быть отрицательной`
            }
        }

        return ''
    }, [billingSettings])

    const handleSaveBillingSettings = async () => {
        if (!billingSettings || billingSettingsLoading || billingSettingsValidationError) return

        setBillingSettingsLoading(true)
        setBillingSettingsError('')
        setBillingSettingsMessage('')

        try {
            const saved = await saveBillingSettings({ office: billingSettings })

            if (!saved) {
                throw new Error('Backend не вернул сохранённые настройки')
            }

            setBillingSettings(saved)
            setBillingSettingsMessage('Настройки биллинга сохранены')
        } catch (error) {
            setBillingSettingsError(
                error?.response?.data?.error
                || error?.response?.data?.message
                || error?.message
                || 'Ошибка сохранения настроек'
            )
        } finally {
            setBillingSettingsLoading(false)
        }
    }

    const renderBillingSettings = () => {
        if (billingSettingsLoading && !billingSettings) {
            return <div className="admin-billing__loader"><Loader /></div>
        }

        if (!billingSettings) {
            return (
                <div className="admin-billing__error" role="alert">
                    <strong>Настройки биллинга не загружены</strong>
                    <span>{billingSettingsError || 'Backend не вернул данные.'}</span>
                </div>
            )
        }

        const numberField = (label, key, suffix = '', min = 0) => (
            <label className="admin-billing__settings-field" key={key}>
                <span>{label}</span>
                <div>
                    <input
                        type="number"
                        min={min}
                        value={billingSettings[key] ?? ''}
                        onChange={(e) => patchBillingSettings(key, Number(e.target.value))}
                        disabled={billingSettingsLoading}
                    />
                    {suffix ? <small>{suffix}</small> : null}
                </div>
            </label>
        )

        return (
            <section className="admin-billing__settings-panel">
                <div className="admin-billing__settings-head">
                    <div>
                        <strong>Тариф офиса и лимиты</strong>
                        <span>
                            Глобальные значения Billing V2. Изменения применяются к новым расчётам после сохранения.
                        </span>
                    </div>
                    <span className="admin-billing__settings-badge">OFFICE BILLING V2</span>
                </div>

                <div className="admin-billing__settings-body">
                    <div className="admin-billing__settings-grid">
                        {numberField('Подписка офиса', 'monthlyPrice', '₽ / период')}
                        {numberField('Длительность периода', 'periodDays', 'дней', 1)}
                        {numberField('Сцен включено', 'includedScenes', 'шт.')}
                        {numberField('Сцена сверх лимита', 'extraScenePrice', '₽')}
                        {numberField('Кредитный лимит по умолчанию', 'defaultCreditLimit', '₽')}
                        {numberField('Максимум сотрудников', 'maxWorkers', 'чел.', 1)}
                        {numberField('Базовое хранилище', 'includedStorageGb', 'ГБ', 1)}
                        {numberField('Цена дополнительного места', 'extraStoragePricePerGb', '₽ / ГБ / период')}
                    </div>

                    <div className="admin-billing__settings-note">
                        <strong>Пакеты хранилища:</strong>{' '}
                        +10 ГБ, +25 ГБ, +50 ГБ и +100 ГБ. Пользователь может только увеличивать объём.
                        При подключении в середине периода списывается пропорциональная часть,
                        затем полная стоимость дополнительного места входит в каждое продление.
                    </div>

                    <div className="admin-billing__settings-packages">
                        <div className="admin-billing__settings-packages-head">
                            <strong>Пакеты сцен</strong>
                            <span>Ключ пакета системный и здесь не меняется.</span>
                        </div>

                        {(billingSettings.scenePackages || []).map((item, index) => (
                            <div className="admin-billing__settings-package" key={item.key || index}>
                                <div className="admin-billing__settings-package-title">
                                    <input
                                        type="text"
                                        value={item.title || ''}
                                        onChange={(e) => patchScenePackage(index, 'title', e.target.value)}
                                        placeholder="Название"
                                        disabled={billingSettingsLoading}
                                    />
                                    <small>{item.key || 'Без ключа'}</small>
                                </div>

                                <label>
                                    <span>Сцен</span>
                                    <input
                                        type="number"
                                        min="1"
                                        value={item.scenes ?? ''}
                                        onChange={(e) => patchScenePackage(index, 'scenes', Number(e.target.value))}
                                        disabled={billingSettingsLoading}
                                    />
                                </label>

                                <label>
                                    <span>Цена</span>
                                    <input
                                        type="number"
                                        min="0"
                                        value={item.price ?? ''}
                                        onChange={(e) => patchScenePackage(index, 'price', Number(e.target.value))}
                                        disabled={billingSettingsLoading}
                                    />
                                </label>

                                <label className="admin-billing__settings-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={item.isActive !== false}
                                        onChange={(e) => patchScenePackage(index, 'isActive', e.target.checked)}
                                        disabled={billingSettingsLoading}
                                    />
                                    <span>Активен</span>
                                </label>
                            </div>
                        ))}
                    </div>

                    <div className="admin-billing__settings-note">
                        <strong>Правило сцен:</strong>{' '}
                        {Number(billingSettings.includedScenes || 0)} включённых сцен обновляются каждый период и не копятся.
                        Пакетные сцены переносятся при продлении без ухода баланса в минус.
                    </div>

                    {billingSettingsValidationError ? (
                        <div className="admin-billing__settings-feedback is-error">
                            {billingSettingsValidationError}
                        </div>
                    ) : null}

                    {billingSettingsError ? (
                        <div className="admin-billing__settings-feedback is-error">
                            {billingSettingsError}
                        </div>
                    ) : null}

                    {billingSettingsMessage ? (
                        <div className="admin-billing__settings-feedback is-success">
                            {billingSettingsMessage}
                        </div>
                    ) : null}

                    <div className="admin-billing__settings-actions">
                        <button
                            type="button"
                            className="admin-billing__primary-btn"
                            onClick={handleSaveBillingSettings}
                            disabled={billingSettingsLoading || Boolean(billingSettingsValidationError)}
                        >
                            {billingSettingsLoading ? 'Сохранение...' : 'Сохранить настройки'}
                        </button>
                    </div>
                </div>
            </section>
        )
    }


    const renderTransactions = () => {
        if (transactions.length === 0) {
            return (
                <div className="admin-billing__empty">
                    <strong>Транзакций пока нет</strong>
                    <span>Здесь появятся движения личных и офисных балансов.</span>
                </div>
            )
        }

        return (
            <div className="admin-billing__content">
                <div className="admin-billing__section-head">
                    <div>
                        <strong>Внутренние транзакции</strong>
                        <span>История начислений, списаний, переводов и покупок с баланса.</span>
                    </div>
                    <span className="admin-billing__counter">{totalAll || transactions.length}</span>
                </div>

                <div className="admin-billing__table-scroll">
                    <table className="admin-billing__table">
                        <thead>
                            <tr>
                                <th>Дата</th>
                                <th>Субъект</th>
                                <th>Сумма</th>
                                <th>Тип</th>
                                <th>Статус</th>
                                <th>Баланс</th>
                                <th>Промокод</th>
                                <th>Описание</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map(tx => {
                                const amount = Number(tx.amount || 0)
                                const typeLabel = TRANSACTION_TYPE_LABELS[tx.type] || tx.type || '—'
                                const targetName = tx.targetId?.firstName
                                    || tx.targetId?.name
                                    || tx.targetId?.email
                                    || tx.targetId
                                    || '—'
                                const targetLabel = tx.targetModel === 'Office'
                                    ? 'Офис'
                                    : tx.targetModel === 'User'
                                        ? 'Пользователь'
                                        : tx.targetModel || '—'

                                return (
                                    <tr key={tx._id}>
                                        <td>{formatDate(tx.createdAt)}</td>
                                        <td>
                                            <div className="admin-billing__subject">
                                                <small>{targetLabel}</small>
                                                <strong>{targetName}</strong>
                                            </div>
                                        </td>
                                        <td className={
                                            amount > 0
                                                ? 'admin-billing__text--positive'
                                                : amount < 0
                                                    ? 'admin-billing__text--negative'
                                                    : 'admin-billing__text--neutral'
                                        }>
                                            {amount > 0 ? '+' : ''}{formatMoney(amount)}
                                        </td>
                                        <td>
                                            <span className="admin-billing__type-label" title={tx.type || ''}>
                                                {typeLabel}
                                            </span>
                                        </td>
                                        <td>{renderTransactionStatus(tx.status)}</td>
                                        <td>
                                            <span className="admin-billing__balance-flow">
                                                {tx.balanceBefore !== null && tx.balanceBefore !== undefined
                                                    ? formatMoney(tx.balanceBefore)
                                                    : '—'}
                                                <b>→</b>
                                                {tx.balanceAfter !== null && tx.balanceAfter !== undefined
                                                    ? formatMoney(tx.balanceAfter)
                                                    : '—'}
                                            </span>
                                        </td>
                                        <td>
                                            {tx.promoCode?.code
                                                ? <span className="admin-billing__promo-code">{tx.promoCode.code}</span>
                                                : '—'}
                                        </td>
                                        <td className="admin-billing__description">{tx.description || '—'}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        )
    }

    const renderPromos = () => {
        return (
            <div className="admin-billing__content">
                <div className="admin-billing__status-filters" role="group" aria-label="Фильтр промокодов">
                    {PROMO_FILTERS.map(filter => (
                        <button
                            key={filter.value}
                            type="button"
                            className={statusFilter === filter.value ? 'active' : ''}
                            aria-pressed={statusFilter === filter.value}
                            onClick={() => setStatusFilter(filter.value)}
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>

                <div className="admin-billing__analytics-card">
                    <div className="admin-billing__analytics-card-icon" aria-hidden="true">₽</div>
                    <div className="admin-billing__analytics-card-info">
                        <span>Начислено бонусами</span>
                        <strong>{formatMoney(analytics.totalVirtualMoneyGiven || 0)}</strong>
                        <small>Фактическая сумма внутреннего баланса, выданная через промокоды.</small>
                    </div>
                </div>

                {promos.length === 0 ? (
                    <div className="admin-billing__empty">
                        <strong>Промокоды не найдены</strong>
                        <span>Измени фильтр или поисковый запрос либо создай новый промокод.</span>
                    </div>
                ) : (
                    <>
                        <div className="admin-billing__section-head">
                            <div>
                                <strong>Промокоды</strong>
                                <span>Бонусы на баланс, проценты к пополнению, подписки и скидки на 3D-сцену.</span>
                            </div>
                            <span className="admin-billing__counter">{totalAll || promos.length}</span>
                        </div>

                        <div className="admin-billing__table-scroll">
                            <table className="admin-billing__table">
                                <thead>
                                    <tr>
                                        <th>Код</th>
                                        <th>Тип / бонус</th>
                                        <th>Область</th>
                                        <th>Статус</th>
                                        <th>Источник</th>
                                        <th>Использований</th>
                                        <th>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {promos.map(p => {
                                        const { usedBy, statusText, statusTone, typeLabel } = getPromoPresentation(p)

                                        return (
                                            <tr key={p._id}>
                                                <td>
                                                    <span className="admin-billing__promo-code admin-billing__promo-code--large">
                                                        {p.code}
                                                    </span>
                                                </td>
                                                <td className="admin-billing__table-strong">{typeLabel}</td>
                                                <td>{PROMO_SCOPE_LABELS[p.scope] || p.scope || '—'}</td>
                                                <td>
                                                    <span className={`admin-billing__status-chip admin-billing__status-chip--${statusTone}`}>
                                                        {statusText}
                                                    </span>
                                                </td>
                                                <td>{p.source || '—'}</td>
                                                <td>{usedBy.length} / {p.maxUses === 0 ? '∞' : p.maxUses}</td>
                                                <td>
                                                    <div className="admin-billing__actions">
                                                        <button
                                                            type="button"
                                                            className="admin-billing__actions-btn--default"
                                                            onClick={() => setSelectedPromoDetails(p)}
                                                        >
                                                            Детали
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="admin-billing__actions-btn--edit"
                                                            onClick={() => handleOpenEdit(p)}
                                                        >
                                                            Изменить
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`admin-billing__actions-btn--toggle ${p.isActive ? 'active' : 'inactive'}`}
                                                            onClick={() => handleTogglePromo(p._id)}
                                                        >
                                                            {p.isActive ? 'Выключить' : 'Включить'}
                                                        </button>
                                                        {!p.isArchived && (
                                                            <button
                                                                type="button"
                                                                className="admin-billing__actions-btn--archive"
                                                                onClick={() => handleArchivePromo(p)}
                                                            >
                                                                В архив
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        )
    }

    return (
        <>
            <MainContainer
                title="Биллинг"
                buttonText="Создать промокод"
                handleShow={handleOpenCreate}
                noneCreateButton={activeTab !== 'promos'}
                fluid
                className="admin-billing-page"
                headerActions={<AdminFinanceNav />}
                tabs
                itemsType="adminBilling"
                setValueTabsSort={setActiveTab}
                type={activeTab === 'settings' ? 'minimalFunctional' : undefined}
                typeSort={typeSort}
                get={getAction}
                search={searchAction}
                additionalData={additionalData}
                setSearchMainName={setSearchMainName}
                setPageNumber={setPageNumber}
                setPageSize={setPageSize}
                pageNumber={pageNumber}
                pageSize={pageSize}
                totalAll={totalAll}
                totalPages={totalPages}
                noneSelectUpdatedPictures
            >
                <div className="admin-billing">
                    {activeTab === 'settings' ? (
                        renderBillingSettings()
                    ) : (
                        <>
                            {error ? (
                                <div className="admin-billing__error" role="alert">
                                    <strong>Не удалось загрузить данные</strong>
                                    <span>{String(error)}</span>
                                </div>
                            ) : null}

                            {loading
                                ? <div className="admin-billing__loader"><Loader /></div>
                                : activeTab === 'transactions'
                                    ? renderTransactions()
                                    : renderPromos()}
                        </>
                    )}
                </div>
            </MainContainer>

            <Modal
                show={showPromoModal}
                handleClose={handleClosePromo}
                modalTitle={promoForm._id ? 'Редактирование промокода' : 'Новый промокод'}
                size="md"
                onSubmit={handleSavePromo}
                currentId={promoForm._id}
                disabledSubmit={!promoFormIsValid || loading}
                confirmText={loading ? 'Сохранение...' : 'Создать промокод'}
                editText={loading ? 'Сохранение...' : 'Сохранить'}
                closeText="Отмена"
                preventOutsideClose={loading}
            >
                <div className="admin-billing__modal">
                    <div className="admin-billing__modal-group">
                        <label htmlFor="billing-promo-code">Код</label>
                        <input
                            id="billing-promo-code"
                            type="text"
                            disabled={!!promoForm._id || loading}
                            value={promoForm.code}
                            onChange={(e) => setPromoForm({
                                ...promoForm,
                                code: e.target.value.toUpperCase()
                            })}
                            placeholder="Например SALE1000"
                            autoComplete="off"
                        />
                        <small>
                            После создания код не меняется. Для нового значения создай новый промокод.
                        </small>
                    </div>

                    <div className="admin-billing__modal-row">
                        <div className="admin-billing__modal-group">
                            <label htmlFor="billing-promo-type">Тип бонуса</label>
                            <select
                                id="billing-promo-type"
                                value={promoForm.type}
                                onChange={(e) => handlePromoTypeChange(e.target.value)}
                                disabled={loading}
                            >
                                <option value="FIXED">Фиксированная сумма ₽</option>
                                <option value="PERCENTAGE">Процент при пополнении %</option>
                                <option value="DISCOUNT">Скидка на сцену ₽</option>
                            </select>
                        </div>

                        <div className="admin-billing__modal-group">
                            <label htmlFor="billing-promo-scope">Область применения</label>
                            <select
                                id="billing-promo-scope"
                                value={promoForm.scope}
                                onChange={(e) => setPromoForm({ ...promoForm, scope: e.target.value })}
                                disabled={loading}
                            >
                                <option value="balance">Начисление на баланс</option>
                                <option value="topup">Бонус при пополнении</option>
                                <option value="scene">Скидка на сцену</option>
                                <option value="any">Любая область</option>
                            </select>
                        </div>
                    </div>

                    {promoForm.type === 'SUBSCRIPTION' ? (
                        <div className="admin-billing__modal-group">
                            <label htmlFor="billing-promo-days">Количество дней</label>
                            <input
                                id="billing-promo-days"
                                type="number"
                                min="1"
                                value={promoForm.subscriptionDays}
                                onChange={(e) => setPromoForm({
                                    ...promoForm,
                                    subscriptionDays: e.target.value
                                })}
                                disabled={loading}
                            />
                        </div>
                    ) : (
                        <div className="admin-billing__modal-group">
                            <label htmlFor="billing-promo-amount">
                                Значение {promoForm.type === 'PERCENTAGE' ? '%' : '₽'}
                            </label>
                            <input
                                id="billing-promo-amount"
                                type="number"
                                min="0"
                                step={promoForm.type === 'PERCENTAGE' ? '1' : '0.01'}
                                value={promoForm.amount}
                                onChange={(e) => setPromoForm({
                                    ...promoForm,
                                    amount: e.target.value
                                })}
                                disabled={loading}
                            />
                        </div>
                    )}

                    {promoForm.type === 'PERCENTAGE' && (
                        <div className="admin-billing__modal-group">
                            <label htmlFor="billing-promo-min-topup">Минимальная сумма пополнения</label>
                            <input
                                id="billing-promo-min-topup"
                                type="number"
                                min="0"
                                step="0.01"
                                value={promoForm.minTopUpAmount}
                                onChange={(e) => setPromoForm({
                                    ...promoForm,
                                    minTopUpAmount: e.target.value
                                })}
                                disabled={loading}
                            />
                        </div>
                    )}

                    <div className="admin-billing__modal-row">
                        <div className="admin-billing__modal-group">
                            <label htmlFor="billing-promo-expire-value">Срок действия</label>
                            <input
                                id="billing-promo-expire-value"
                                type="number"
                                min="0"
                                value={promoForm.expireValue}
                                onChange={(e) => setPromoForm({
                                    ...promoForm,
                                    expireValue: e.target.value
                                })}
                                placeholder="Пусто — бессрочно"
                                disabled={loading}
                            />
                        </div>

                        <div className="admin-billing__modal-group">
                            <label htmlFor="billing-promo-expire-unit">Единица</label>
                            <select
                                id="billing-promo-expire-unit"
                                value={promoForm.expireUnit}
                                onChange={(e) => setPromoForm({
                                    ...promoForm,
                                    expireUnit: e.target.value
                                })}
                                disabled={loading}
                            >
                                <option value="hours">Часов</option>
                                <option value="days">Дней</option>
                                <option value="months">Месяцев</option>
                            </select>
                        </div>
                    </div>

                    <div className="admin-billing__modal-group admin-billing__user-search">
                        <label htmlFor="billing-promo-user">Конкретный пользователь</label>

                        {selectedUser ? (
                            <div className="admin-billing__user-search-selected">
                                <div>
                                    <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>
                                    <span>{selectedUser.email || 'Email не указан'}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSelectedUser(null)}
                                    disabled={loading}
                                >
                                    Отвязать
                                </button>
                            </div>
                        ) : (
                            <>
                                <input
                                    id="billing-promo-user"
                                    type="search"
                                    placeholder="Имя или email — минимум 3 символа"
                                    value={userSearchText}
                                    onChange={handleUserSearch}
                                    autoComplete="off"
                                    disabled={loading}
                                />

                                {userSearchText.length > 2 && usersList.length > 0 && (
                                    <div className="admin-billing__user-search-dropdown">
                                        {usersList.map(u => (
                                            <button
                                                type="button"
                                                key={u._id}
                                                className="admin-billing__user-search-dropdown-item"
                                                onClick={() => {
                                                    setSelectedUser(u)
                                                    setUserSearchText('')
                                                }}
                                            >
                                                <strong>{u.firstName} {u.lastName}</strong>
                                                <span>{u.email}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        <small>
                            Оставь пустым, если промокод должен быть доступен всем подходящим пользователям.
                        </small>
                    </div>

                    <div className="admin-billing__modal-row">
                        <div className="admin-billing__modal-group">
                            <label htmlFor="billing-promo-source">Источник / UTM-метка</label>
                            <input
                                id="billing-promo-source"
                                type="text"
                                placeholder="Например Yandex или Blogger_Ivan"
                                value={promoForm.source}
                                onChange={(e) => setPromoForm({
                                    ...promoForm,
                                    source: e.target.value
                                })}
                                disabled={loading}
                            />
                        </div>

                        <div className="admin-billing__modal-group">
                            <label htmlFor="billing-promo-limit">Лимит активаций</label>
                            <input
                                id="billing-promo-limit"
                                type="number"
                                min="0"
                                value={promoForm.maxUses}
                                onChange={(e) => setPromoForm({
                                    ...promoForm,
                                    maxUses: e.target.value
                                })}
                                disabled={loading}
                            />
                            <small>0 — без ограничений.</small>
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal
                show={Boolean(selectedPromoDetails)}
                handleClose={() => setSelectedPromoDetails(null)}
                modalTitle={selectedPromoDetails ? `Промокод ${selectedPromoDetails.code}` : 'Детали промокода'}
                size="lg"
                disabledFunctional
                closeText="Закрыть"
            >
                {selectedPromoDetails ? (() => {
                    const { usedBy, statusText, statusTone, typeLabel } = getPromoPresentation(selectedPromoDetails)

                    return (
                        <div className="admin-billing__details">
                            <div className="admin-billing__details-summary">
                                <div>
                                    <span>Код</span>
                                    <strong className="admin-billing__promo-code admin-billing__promo-code--large">
                                        {selectedPromoDetails.code}
                                    </strong>
                                </div>
                                <div>
                                    <span>Статус</span>
                                    <strong>
                                        <span className={`admin-billing__status-chip admin-billing__status-chip--${statusTone}`}>
                                            {statusText}
                                        </span>
                                    </strong>
                                </div>
                                <div>
                                    <span>Тип / бонус</span>
                                    <strong>{typeLabel}</strong>
                                </div>
                                <div>
                                    <span>Использований</span>
                                    <strong>{usedBy.length} / {selectedPromoDetails.maxUses === 0 ? '∞' : selectedPromoDetails.maxUses}</strong>
                                </div>
                            </div>

                            <section className="admin-billing__details-section">
                                <h5>Параметры промокода</h5>
                                <dl>
                                    <div>
                                        <dt>Область применения</dt>
                                        <dd>{PROMO_SCOPE_LABELS[selectedPromoDetails.scope] || selectedPromoDetails.scope || '—'}</dd>
                                    </div>
                                    <div>
                                        <dt>Действует до</dt>
                                        <dd>{selectedPromoDetails.expiresAt ? formatDate(selectedPromoDetails.expiresAt) : 'Бессрочно'}</dd>
                                    </div>
                                    <div>
                                        <dt>Минимальное пополнение</dt>
                                        <dd>{formatMoney(selectedPromoDetails.minTopUpAmount || 0)}</dd>
                                    </div>
                                    <div>
                                        <dt>Источник / UTM</dt>
                                        <dd>{selectedPromoDetails.source || '—'}</dd>
                                    </div>
                                    <div>
                                        <dt>Целевой клиент</dt>
                                        <dd>
                                            {selectedPromoDetails.targetUser
                                                ? `${selectedPromoDetails.targetUser.firstName || ''} ${selectedPromoDetails.targetUser.lastName || ''} (${selectedPromoDetails.targetUser.email || ''})`
                                                : 'Любой пользователь'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt>Создал</dt>
                                        <dd>
                                            {selectedPromoDetails.createdBy
                                                ? `${selectedPromoDetails.createdBy.firstName || ''} ${selectedPromoDetails.createdBy.lastName || ''} (${selectedPromoDetails.createdBy.email || ''})`
                                                : '—'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt>Создан</dt>
                                        <dd>{formatDate(selectedPromoDetails.createdAt)}</dd>
                                    </div>
                                    <div>
                                        <dt>Последнее изменение</dt>
                                        <dd>{formatDate(selectedPromoDetails.updatedAt)}</dd>
                                    </div>
                                </dl>
                            </section>

                            <section className="admin-billing__details-section">
                                <div className="admin-billing__details-section-head">
                                    <h5>История использований</h5>
                                    <span>{usedBy.length}</span>
                                </div>

                                {usedBy.length === 0 ? (
                                    <div className="admin-billing__details-empty">
                                        Этот промокод пока никто не использовал.
                                    </div>
                                ) : (
                                    <div className="admin-billing__details-usage-list">
                                        {usedBy.map((usage, idx) => (
                                            <div
                                                className="admin-billing__details-usage"
                                                key={`${usage.user?._id || idx}-${usage.usedAt || idx}`}
                                            >
                                                <div>
                                                    <strong>
                                                        {usage.user?.firstName} {usage.user?.lastName}
                                                    </strong>
                                                    <span>{usage.user?.email || 'Email не указан'}</span>
                                                </div>
                                                <span>{formatDate(usage.usedAt)}</span>
                                                <strong>+{formatMoney(usage.bonusAmount || 0)}</strong>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>
                        </div>
                    )
                })() : null}
            </Modal>

            <ModalConfirm
                title="Отправить промокод в архив?"
                description={
                    archivePromo
                        ? `Промокод ${archivePromo.code} будет отключён и скрыт из рабочего списка. Вернуть его можно будет из архива.`
                        : ''
                }
                show={Boolean(archivePromo)}
                handleClose={handleCloseArchiveConfirm}
                onConfirm={handleConfirmArchive}
                confirmText={loading ? 'Перемещение...' : 'В архив'}
                cancelText="Отмена"
                confirmVariant="danger"
                disabledConfirm={loading}
            />

        </>
    )
}

export default AdminBillingPage
